import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import baseUrl from "../../../../../../api/baseUrl";
import {
  setloadeActive,
  setloadeDeactive,
} from "../../../../../../redux/loader/loaderActions";
import { useDispatch } from "react-redux";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import FormCheck from "../../../../../../components/formFeild/FormCheck";
import { toast } from "react-toastify";

interface FormData {
  payments: boolean;
}

function PastInvoice() {
  const { ID } = useParams();
  const navigate = useNavigate();
  const [openpayments, setopenpayments] = useState(false);
  const [payment, setpayment] = useState([]);
  const methods = useForm<FormData>();
  const { watch, handleSubmit } = methods;
  const dispatch = useDispatch();

  const getData = useCallback(
    (treatmentId: string) => {
      if (!treatmentId) return;
      
      dispatch(setloadeActive());

      // Get all payments by treatment ID (API will find treatment and get patient ID)
      baseUrl
        .get("treatment/getPaymentsByPatient", {
          headers: {
            treatmentid: treatmentId,
          },
        })
        .then((response) => {
          dispatch(setloadeDeactive());
          setpayment(response.data.data || []);
        })
        .catch((error) => {
          dispatch(setloadeDeactive());
          console.log(error);
          setpayment([]);
        });
    },
    [dispatch]
  );

  useEffect(() => {
    if (ID) {
      getData(ID);
    }
  }, [ID, getData]);

  useEffect(() => {
    const { unsubscribe } = watch((value) => {
      if (value.payments) {
        setopenpayments(true);
      } else {
        setopenpayments(false);
      }
    });
    return () => unsubscribe();
  }, [watch]);

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (data) => {
      if (!ID) {
        toast.error("Treatment ID not found");
        return;
      }

      try {
        const response = await baseUrl.get("treatment/generatePaymentReport", {
          responseType: "blob",
          headers: {
            treatmentid: ID,
          },
          params: {
            payments: data.payments,
          },
        });
        if (response.status === 204) {
          toast("No data found");
          return;
        }
        const pdfBlob = new Blob([response.data], { type: "application/pdf" });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(pdfBlob);
        link.download = "patient_payment_report.pdf";
        document.body.appendChild(link);

        link.click();

        URL.revokeObjectURL(link.href);
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error downloading the PDF", error);
        toast.error("Error downloading PDF");
      }
    },
    [ID]
  );

  function paymentMethod(card: number, cash: number, bank: number) {
    const methods = [];
    if (card > 0) methods.push("card");
    if (cash > 0) methods.push("cash");
    if (bank > 0) methods.push("bank");

    return methods.length > 0 ? methods.join("/") : "none";
  }

  return (
    <div className="space-y-2.5">
      <style>
        {`
          .form__group {
            margin: 0 !important;
            gap: 4px !important;
          }
          .form__group .form__field {
            height: 32px !important;
            padding: 0px 10px !important;
            font-size: 12px !important;
            border-radius: 6px !important;
            border: 1px solid #e2e8f0 !important;
          }
          .form__group .form__label {
            font-size: 11px !important;
            font-weight: 500 !important;
            margin-bottom: 0 !important;
            color: #475569 !important;
          }
          .form__group__check {
            margin: 0 !important;
            gap: 6px !important;
            align-items: center !important;
          }
          .form__group__check span {
            font-size: 11px !important;
            font-weight: 500 !important;
            color: #475569 !important;
          }
          .form__group__check .w-10 {
            width: 1.75rem !important;
            height: 1rem !important;
            flex-shrink: 0 !important;
          }
          .form__group__check .after\\:h-4 {
            height: 0.75rem !important;
            width: 0.75rem !important;
          }
          .form__group__check .after\\:top-\\[2px\\] {
            top: 1px !important;
            left: 1px !important;
          }
          .form__group__check .peer-checked\\:after\\:translate-x-full {
            transform: translateX(0.75rem) !important;
          }
        `}
      </style>
      {/* Filter Form */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-1">
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <div className="flex items-center min-w-0 flex-1">
              <FormCheck label="Payment Breakdown" name="payments" />
            </div>
            <button
              type="submit"
              className="w-full sm:w-auto px-6 py-2.5 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-semibold hover:shadow-lg hover:shadow-primary/30 transition-all text-sm whitespace-nowrap flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98]"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Generate Report
            </button>
          </form>
        </FormProvider>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-[8px] shadow-lg shadow-slate-200/50 border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-200">
              <tr>
                <th className="px-2.5 py-1.5 text-left text-xs font-semibold text-slate-500 uppercase">
                  Invoice
                </th>
                <th className="px-2.5 py-1.5 text-left text-xs font-semibold text-slate-500 uppercase">
                  Date
                </th>
                <th className="px-2.5 py-1.5 text-left text-xs font-semibold text-slate-500 uppercase">
                  Patient
                </th>
                <th className="px-2.5 py-1.5 text-left text-xs font-semibold text-slate-500 uppercase">
                  Dentist
                </th>
                <th className="px-2.5 py-1.5 text-left text-xs font-semibold text-slate-500 uppercase">
                  Total
                </th>
                <th className="px-2.5 py-1.5 text-left text-xs font-semibold text-slate-500 uppercase">
                  Paid
                </th>
                <th className="px-2.5 py-1.5 text-left text-xs font-semibold text-slate-500 uppercase">
                  Discount
                </th>
                <th className="px-2.5 py-1.5 text-left text-xs font-semibold text-slate-500 uppercase">
                  Method
                </th>
                <th className="px-2.5 py-1.5 text-left text-xs font-semibold text-slate-500 uppercase">
                  Due/Adv
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {payment.map((item: any, index) => {
                const paidAmount = item?.paymentInfo?.reduce(
                  (acc: any, curr: any) =>
                    acc + curr.card + curr.cash + curr.bankTransfer,
                  0
                );
                const discountAmount = item?.paymentInfo?.reduce(
                  (acc: any, curr: any) => acc + curr.discount,
                  0
                );
                const dueAmount =
                  item?.totalPrice -
                  item?.paymentInfo?.reduce(
                    (acc: any, curr: any) =>
                      acc +
                      curr.card +
                      curr.cash +
                      curr.bankTransfer +
                      curr.discount,
                    0
                  );
                const { card = 0, cash = 0, bankTransfer = 0 } =
                  item?.paymentInfo?.reduce(
                    (acc: any, curr: any) => ({
                      card: acc.card + curr.card,
                      cash: acc.cash + curr.cash,
                      bankTransfer: acc.bankTransfer + curr.bankTransfer,
                    }),
                    { card: 0, cash: 0, bankTransfer: 0 }
                  );
                const methodStr = [
                  card > 0 && "Card",
                  cash > 0 && "Cash",
                  bankTransfer > 0 && "Bank",
                ]
                  .filter(Boolean)
                  .join("/");

                return (
                  <React.Fragment key={index}>
                    <tr
                      onClick={() =>
                        navigate(`/client/payments/invoice/${item?._id}`)
                      }
                      className="hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <td className="px-2.5 py-1.5 text-xs font-medium text-indigo-600">
                        {item?.invoiceNo}
                      </td>
                      <td className="px-2.5 py-1.5 text-xs text-slate-600">
                        {item?.updatedAt?.split("T")[0]}
                      </td>
                      <td className="px-2.5 py-1.5 text-xs font-medium text-slate-800">
                        {item?.fullName}
                      </td>
                      <td className="px-2.5 py-1.5 text-xs text-slate-600">
                        {item?.dentistName} {item?.dentistLastName}
                      </td>
                      <td className="px-2.5 py-1.5 text-xs text-slate-600">
                        {item?.totalPrice}
                      </td>
                      <td className="px-2.5 py-1.5 text-xs font-semibold text-emerald-600">
                        {paidAmount}
                      </td>
                      <td className="px-2.5 py-1.5 text-xs text-orange-600">
                        {discountAmount}
                      </td>
                      <td className="px-2.5 py-1.5">
                        <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-md text-[10px] font-medium">
                          {methodStr || "—"}
                        </span>
                      </td>
                      <td
                        className={`px-2.5 py-1.5 text-xs font-semibold ${
                          dueAmount > 0 ? "text-red-600" : "text-emerald-600"
                        }`}
                      >
                        {dueAmount}
                      </td>
                    </tr>
                    {openpayments &&
                      item?.paymentInfo?.map((items: any, indexa: number) => (
                        <tr key={indexa} className="bg-slate-50/30">
                          <td className="px-2.5 py-1 pl-5 text-[10px] text-slate-500">
                            {items?.invoiceNo}
                          </td>
                          <td className="px-2.5 py-1 text-[10px] text-slate-500">
                            {items?.updatedAt?.split("T")[0]}
                          </td>
                          <td className="px-2.5 py-1"></td>
                          <td className="px-2.5 py-1"></td>
                          <td className="px-2.5 py-1 text-[10px] text-slate-600">
                            {items?.payable}
                          </td>
                          <td className="px-2.5 py-1 text-[10px] text-emerald-600">
                            {items?.cash + items?.card + items?.bankTransfer}
                          </td>
                          <td className="px-2.5 py-1 text-[10px] text-orange-500">
                            {items?.discount}
                          </td>
                          <td className="px-2.5 py-1 text-[10px] text-slate-500">
                            {paymentMethod(
                              items?.card,
                              items?.cash,
                              items?.bankTransfer
                            )}
                          </td>
                          <td
                            className={`px-2.5 py-1 text-[10px] font-semibold ${
                              items?.due > 0
                                ? "text-red-600 bg-red-50"
                                : "text-emerald-600 bg-emerald-50"
                            } rounded`}
                          >
                            {items?.due}
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-2">
        {payment.map((item: any, index) => {
          const paidAmount = item?.paymentInfo?.reduce(
            (acc: any, curr: any) =>
              acc + curr.card + curr.cash + curr.bankTransfer,
            0
          );
          const dueAmount =
            item?.totalPrice -
            item?.paymentInfo?.reduce(
              (acc: any, curr: any) =>
                acc +
                curr.card +
                curr.cash +
                curr.bankTransfer +
                curr.discount,
              0
            );
          return (
            <div
              key={index}
              onClick={() => navigate(`/client/payments/invoice/${item?._id}`)}
              className="bg-white rounded-lg shadow-sm border border-slate-100 p-3 cursor-pointer hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-medium text-xs text-indigo-600">
                  {item?.invoiceNo}
                </span>
                <span className="text-[10px] text-slate-500">
                  {item?.updatedAt?.split("T")[0]}
                </span>
              </div>
              <h3 className="font-semibold text-xs text-slate-800 mb-1.5">
                {item?.fullName}
              </h3>
              <div className="grid grid-cols-3 gap-1.5 text-xs">
                <div className="bg-slate-50 rounded-md p-1.5 text-center">
                  <p className="text-[10px] text-slate-500">Total</p>
                  <p className="font-semibold text-slate-700">
                    {item?.totalPrice}
                  </p>
                </div>
                <div className="bg-emerald-50 rounded-md p-1.5 text-center">
                  <p className="text-[10px] text-emerald-600">Paid</p>
                  <p className="font-semibold text-emerald-700">
                    {paidAmount}
                  </p>
                </div>
                <div
                  className={`rounded-md p-1.5 text-center ${
                    dueAmount > 0 ? "bg-red-50" : "bg-emerald-50"
                  }`}
                >
                  <p
                    className={`text-[10px] ${
                      dueAmount > 0 ? "text-red-600" : "text-emerald-600"
                    }`}
                  >
                    {dueAmount > 0 ? "Due" : "Adv"}
                  </p>
                  <p
                    className={`font-semibold ${
                      dueAmount > 0 ? "text-red-700" : "text-emerald-700"
                    }`}
                  >
                    {dueAmount}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PastInvoice;
